import React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { BehaviorEntry } from "../../types/tradingJournal";
import { Edit2Icon } from "lucide-react";
import { formatDate } from "../../lib/utils";

interface BehaviorEntriesTableProps {
  filteredBehaviorEntries: BehaviorEntry[];
  onEditEntry: (entry: BehaviorEntry) => void;
  onAddEntry: () => void;
}

const BehaviorEntriesTable = ({
  filteredBehaviorEntries,
  onEditEntry,
  onAddEntry,
}: BehaviorEntriesTableProps) => {
  return (
    <Card className="intellectia-card bg-white/5 mt-8">
      <CardHeader>
        <CardTitle>Recent Journal Entries</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-white/70 font-medium">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">
                  P&L
                </th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">
                  Plan
                </th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">
                  Cut Losses
                </th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">
                  Emotional
                </th>
                <th className="text-left py-3 px-4 text-white/70 font-medium">
                  Setup
                </th>
                <th className="text-right py-3 px-4 text-white/70 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredBehaviorEntries.map((entry) => (
                <tr
                  key={entry.id}
                  className="border-b border-white/5 hover:bg-white/5"
                >
                  <td className="py-3 px-4">{formatDate(entry.date)}</td>
                  <td
                    className={`py-3 px-4 ${
                      entry.dailyPnL >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    ₹{entry.dailyPnL.toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs ${
                        entry.followedPlan === "Yes"
                          ? "bg-green-900/30 text-green-400"
                          : entry.followedPlan === "Partially"
                          ? "bg-yellow-900/30 text-yellow-400"
                          : "bg-red-900/30 text-red-400"
                      }`}
                    >
                      {entry.followedPlan}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs ${
                        entry.cutLossesQuickly === "Yes"
                          ? "bg-green-900/30 text-green-400"
                          : entry.cutLossesQuickly === "Partially"
                          ? "bg-yellow-900/30 text-yellow-400"
                          : "bg-red-900/30 text-red-400"
                      }`}
                    >
                      {entry.cutLossesQuickly}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs ${
                        entry.emotionallyCalm === "Yes"
                          ? "bg-green-900/30 text-green-400"
                          : entry.emotionallyCalm === "Partially"
                          ? "bg-yellow-900/30 text-yellow-400"
                          : "bg-red-900/30 text-red-400"
                      }`}
                    >
                      {entry.emotionallyCalm}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center justify-center px-2 py-1 rounded-full text-xs ${
                        entry.tradedOnSetup === "Setup"
                          ? "bg-green-900/30 text-green-400"
                          : entry.tradedOnSetup === "Both"
                          ? "bg-blue-900/30 text-blue-400"
                          : "bg-yellow-900/30 text-yellow-400"
                      }`}
                    >
                      {entry.tradedOnSetup}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditEntry(entry)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2Icon className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredBehaviorEntries.length === 0 && (
            <div className="text-center py-8">
              <Button
                onClick={onAddEntry}
                variant="outline"
                className="mt-4 bg-white/5 border-white/10"
              >
                Add First Entry
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BehaviorEntriesTable;
